import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import TrailPainting from '../components/TrailPainting';
import TimezoneClock from '../components/TimezoneClock';
import DaysCalendar from '../components/DaysCalendar';
import PictureFrame from '../components/PictureFrame';
import YoSign from '../components/YoSign';
import SharedDesk from '../components/SharedDesk';
import ScrollTransition from '../components/ScrollTransition';
import FootprintPainting from '../components/FootprintPainting';
import PinnedStickyNote from '../components/PinnedStickyNote';
import OrangeExplosion from '../components/OrangeExplosion';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [isLoaded, setIsLoaded] = useState(false);
  const [showDeskButtons, setShowDeskButtons] = useState(false);
  const deskSectionRef = useRef(null);

  // Punishment popup state - use sessionStorage so dismissal persists across pages
  const [activePunishment, setActivePunishment] = useState(null);
  const [showPunishmentPopup, setShowPunishmentPopup] = useState(false);
  const [punishmentPopupDismissed, setPunishmentPopupDismissed] = useState(() => {
    return sessionStorage.getItem('punishmentPopupDismissed') === 'true';
  });
  // Check if this is a fresh login (5s fade) or returning to page (2s fade)
  const isFreshLogin = location.state?.freshLogin === true;
  const fadeDuration = isFreshLogin ? 5 : 2;
  const [wallLayout, setWallLayout] = useState({
    painting: { gridColumn: '2 / 6', gridRow: '2 / 6' },
    omiClock: { gridColumn: '7 / 9', gridRow: '2 / 4' },
    henryClock: { gridColumn: '10 / 12', gridRow: '2 / 4' },
    calendar: { gridColumn: '11 / 13', gridRow: '4 / 7' },
    frame: { gridColumn: '2 / 5', gridRow: '6 / 9' },
    footprintPainting: { gridColumn: '7 / 9', gridRow: '4 / 7' },
    yoSign: { gridColumn: '11 / 13', gridRow: '7 / 8' },
    ottoShelf: { gridColumn: '1 / 2', gridRow: '1 / 3' }
  });
  const [pinnedNotes, setPinnedNotes] = useState({});
  const [showOrangeExplosion, setShowOrangeExplosion] = useState(false);
  const [ottoWaving, setOttoWaving] = useState(false);

  // useLayoutEffect runs BEFORE paint, ensuring scroll happens before user sees anything
  useLayoutEffect(() => {
    // Force scroll to top immediately, synchronously, before browser paints
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    // Load wall layout from Firebase
    const layoutRef = ref(database, 'wallLayouts/default');
    const unsubscribe = onValue(layoutRef, (snapshot) => {
      if (snapshot.exists()) {
        setWallLayout(snapshot.val());
      }
    });

    return () => unsubscribe();
  }, []);

  // Load pinned notes from Firebase
  useEffect(() => {
    const pinnedRef = ref(database, 'wall/pinnedNotes');
    const unsubscribe = onValue(pinnedRef, (snapshot) => {
      if (snapshot.exists()) {
        setPinnedNotes(snapshot.val());
      } else {
        setPinnedNotes({});
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Force scroll to top on mount and after any layout shifts
    const forceScrollTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    forceScrollTop();

    // Continue forcing scroll for first 100ms to catch layout shifts
    const scrollInterval = setInterval(forceScrollTop, 10);
    setTimeout(() => clearInterval(scrollInterval), 100);

    // Trigger unblur transition after a brief moment - extended delay for smoother loading disguise
    const loadTimer = setTimeout(() => {
      setIsLoaded(true);
    }, 300);

    return () => {
      clearInterval(scrollInterval);
      clearTimeout(loadTimer);
    };
  }, []);

  // Make Otto wave after 20 seconds on homepage
  useEffect(() => {
    const waveTimer = setTimeout(() => {
      setOttoWaving(true);
      // Stop waving after 2 seconds
      setTimeout(() => setOttoWaving(false), 2000);
    }, 20000);

    return () => clearTimeout(waveTimer);
  }, []);

  // Listen for active punishment (Omi only) - show popup on login
  useEffect(() => {
    if (userProfile?.name !== 'Omi') return;

    const activePunishmentRef = ref(database, 'intimacy/freaky/activePunishment');
    const unsubscribe = onValue(activePunishmentRef, (snapshot) => {
      if (snapshot.exists()) {
        setActivePunishment(snapshot.val());
        // Show popup if not already dismissed this session
        if (!punishmentPopupDismissed) {
          setShowPunishmentPopup(true);
        }
      } else {
        setActivePunishment(null);
        setShowPunishmentPopup(false);
      }
    });

    return () => unsubscribe();
  }, [userProfile, punishmentPopupDismissed]);

  // Track scroll position to show buttons
  useEffect(() => {
    const handleScroll = () => {
      if (!deskSectionRef.current) return;

      const deskRect = deskSectionRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Show buttons when the bottom of the desk section is near the top of the viewport
      const shouldShowButtons = deskRect.bottom < viewportHeight * 0.85;
      setShowDeskButtons(shouldShowButtons);
    };

    // Delay initial check to ensure ref is mounted
    const timer = setTimeout(handleScroll, 500);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {/* Wall Section */}
      <div
        className={`home-page ${isLoaded ? 'loaded' : 'loading'}`}
        style={{
          transition: `filter ${fadeDuration}s ease-out, opacity ${fadeDuration}s ease-out`
        }}
      >
        <div className="wall-background">
          <div className="wall-texture" />
          <div className="wall-shadow" />
        </div>

        <div className="wall-elements">
          <div
            className="painting-container"
            style={{
              gridColumn: wallLayout.painting?.gridColumn,
              gridRow: wallLayout.painting?.gridRow
            }}
          >
            <TrailPainting />
          </div>

          {/* Omi's Timezone Clock */}
          <div
            className="omi-clock-container"
            style={{
              gridColumn: wallLayout.omiClock?.gridColumn,
              gridRow: wallLayout.omiClock?.gridRow
            }}
          >
            <TimezoneClock userName="Omi" />
          </div>

          {/* Henry's Timezone Clock */}
          <div
            className="henry-clock-container"
            style={{
              gridColumn: wallLayout.henryClock?.gridColumn,
              gridRow: wallLayout.henryClock?.gridRow
            }}
          >
            <TimezoneClock userName="Henry" />
          </div>

          {/* Days Together Calendar */}
          <div
            className="calendar-container"
            style={{
              gridColumn: wallLayout.calendar?.gridColumn,
              gridRow: wallLayout.calendar?.gridRow
            }}
          >
            <DaysCalendar />
          </div>

          <div
            className="frame-container"
            style={{
              gridColumn: wallLayout.frame?.gridColumn,
              gridRow: wallLayout.frame?.gridRow
            }}
          >
            <PictureFrame />
          </div>

          {/* Footprint Canvas Painting - Under Omi's Clock */}
          <div
            className="footprint-painting-container"
            style={{
              gridColumn: wallLayout.footprintPainting?.gridColumn,
              gridRow: wallLayout.footprintPainting?.gridRow
            }}
          >
            <FootprintPainting />
          </div>

          {/* Feature 8: YO Sign */}
          <div
            className="yo-sign-wall-container"
            style={{
              gridColumn: wallLayout.yoSign?.gridColumn,
              gridRow: wallLayout.yoSign?.gridRow
            }}
          >
            <YoSign />
          </div>

          {/* Otto the Orange on a shelf */}
          <div
            className="otto-shelf-container"
            style={{
              gridColumn: wallLayout.ottoShelf?.gridColumn,
              gridRow: wallLayout.ottoShelf?.gridRow
            }}
          >
            <div className="otto-shelf">
              <img
                src="/otto-compressed.png"
                alt="Otto the Orange"
                className={`otto-plush ${ottoWaving ? 'waving' : ''}`}
                onClick={() => setShowOrangeExplosion(true)}
                style={{ cursor: 'pointer' }}
              />
              <div className="shelf-board" />
              <div className="shelf-bracket left" />
              <div className="shelf-bracket right" />
            </div>
          </div>
        </div>

        {/* Pinned Sticky Notes - overlay on top of wall */}
        <div className="pinned-notes-overlay">
          {Object.entries(pinnedNotes).map(([id, note]) => (
            <PinnedStickyNote
              key={id}
              note={note}
              position={note.wallPosition || { x: 10, y: 50, rotation: 0 }}
            />
          ))}
        </div>

        {/* Scroll Transition with Sparkles */}
        <ScrollTransition />

        {/* Desk Section - Shared collaborative workspace */}
        <div ref={deskSectionRef} className="desk-section">
          <SharedDesk showNavButtons={false} />
        </div>

        {/* Daytime Forest - Navigation buttons */}
        <div className="below-desk-nav">
          <AnimatePresence mode="wait">
            {!showDeskButtons ? (
              <motion.div
                key="scroll-hint"
                className="desk-scroll-hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  className="hint-arrow"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  ↓
                </motion.div>
                <p>Scroll to explore more</p>
              </motion.div>
            ) : (
              <motion.div
                key="nav-buttons"
                className="desk-nav-buttons"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Letters button - cute parchment envelope from left */}
                <motion.button
                  className="desk-nav-button letters-button"
                  onClick={() => navigate('/letters')}
                  initial={{ opacity: 0, x: -200, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ duration: 0.5, type: "spring", stiffness: 100, damping: 15 }}
                  whileHover={{ scale: 1.08, y: -8 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg viewBox="0 0 60 50" className="button-icon">
                    {/* Parchment envelope body */}
                    <path
                      d="M 5,15 L 55,15 L 55,45 L 5,45 Z"
                      fill="#f8f4e8"
                      stroke="#c4a77d"
                      strokeWidth="2"
                    />
                    {/* Envelope flap */}
                    <path
                      d="M 5,15 L 30,32 L 55,15"
                      fill="#efe8d8"
                      stroke="#c4a77d"
                      strokeWidth="2"
                    />
                    {/* Wax seal with heart */}
                    <circle cx="30" cy="28" r="8" fill="#d4737a" stroke="#b85a61" strokeWidth="1.5" />
                    <path
                      d="M 30,24 Q 27,22 25,24 Q 23,27 25,29 L 30,33 L 35,29 Q 37,27 35,24 Q 33,22 30,24 Z"
                      fill="#e89a9f"
                    />
                  </svg>
                  <span>Letters</span>
                </motion.button>

                {/* Tape Recorder button - from bottom */}
                <motion.button
                  className="desk-nav-button cassette-button"
                  onClick={() => navigate('/voice-memos')}
                  initial={{ opacity: 0, y: 150, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.5, type: "spring", stiffness: 100, damping: 15, delay: 0.1 }}
                  whileHover={{ scale: 1.08, y: -8 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg viewBox="0 0 80 70" className="button-icon">
                    {/* Carrying straps - teal/blue */}
                    <rect x="2" y="25" width="8" height="20" rx="2" fill="#88c8c8" />
                    <rect x="70" y="25" width="8" height="20" rx="2" fill="#88c8c8" />
                    <rect x="0" y="28" width="6" height="14" rx="1.5" fill="#a8d8d8" />
                    <rect x="74" y="28" width="6" height="14" rx="1.5" fill="#a8d8d8" />

                    {/* Main wooden body */}
                    <rect x="10" y="12" width="60" height="52" rx="6" fill="#e8d4b8" stroke="#d4c0a0" strokeWidth="1.5" />
                    {/* Wood grain highlight */}
                    <rect x="12" y="14" width="56" height="48" rx="5" fill="#f0e0c8" opacity="0.6" />

                    {/* Top wooden knobs */}
                    {/* Left knob - plain cylinder */}
                    <ellipse cx="28" cy="12" rx="6" ry="2" fill="#d4c0a0" />
                    <rect x="22" y="4" width="12" height="8" rx="2" fill="#e8d4b8" stroke="#d4c0a0" strokeWidth="1" />
                    <ellipse cx="28" cy="4" rx="6" ry="2" fill="#f0e0c8" />

                    {/* Right knob - with triangle marker */}
                    <ellipse cx="52" cy="12" rx="6" ry="2" fill="#d4c0a0" />
                    <rect x="46" y="4" width="12" height="8" rx="2" fill="#e8d4b8" stroke="#d4c0a0" strokeWidth="1" />
                    <ellipse cx="52" cy="4" rx="6" ry="2" fill="#f0e0c8" />
                    <polygon points="52,2 50,5 54,5" fill="#d4c0a0" />

                    {/* Pink front panel */}
                    <rect x="16" y="18" width="48" height="40" rx="4" fill="#fce4ec" stroke="#f0c0d0" strokeWidth="1" />

                    {/* Green cassette door */}
                    <rect x="20" y="24" width="32" height="28" rx="3" fill="#b8e8a8" stroke="#98d888" strokeWidth="1" />

                    {/* Circular speaker grille - dots in circle pattern */}
                    <circle cx="34" cy="38" r="10" fill="#c8f0b8" stroke="#a8d898" strokeWidth="0.8" />
                    {/* Center hole */}
                    <circle cx="34" cy="38" r="2.5" fill="#f8f8f0" stroke="#d0d0c0" strokeWidth="0.5" />
                    {/* Speaker holes in circular pattern */}
                    <g fill="#98c888">
                      <circle cx="34" cy="31" r="1.2" />
                      <circle cx="39" cy="33" r="1.2" />
                      <circle cx="41" cy="38" r="1.2" />
                      <circle cx="39" cy="43" r="1.2" />
                      <circle cx="34" cy="45" r="1.2" />
                      <circle cx="29" cy="43" r="1.2" />
                      <circle cx="27" cy="38" r="1.2" />
                      <circle cx="29" cy="33" r="1.2" />
                    </g>

                    {/* Small dial/knob on right side of green panel */}
                    <circle cx="47" cy="44" r="4" fill="#f8f8f0" stroke="#d0d0c0" strokeWidth="1" />
                    <circle cx="47" cy="44" r="2" fill="#e0e0d8" />
                    <circle cx="47" cy="44" r="0.8" fill="#88c8c8" />

                    {/* Small decorative detail - recording indicator */}
                    <circle cx="58" cy="28" r="2.5" fill="#f8d8d8" stroke="#e8b8b8" strokeWidth="0.5" />
                    <circle cx="58" cy="28" r="1.2" fill="#e88888" />
                  </svg>
                  <span>Voice</span>
                </motion.button>

                {/* Poetry button - from right */}
                <motion.button
                  className="desk-nav-button poetry-button"
                  onClick={() => navigate('/poetry')}
                  initial={{ opacity: 0, x: 200, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ duration: 0.5, type: "spring", stiffness: 100, damping: 15 }}
                  whileHover={{ scale: 1.08, y: -8 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg viewBox="0 0 50 60" className="button-icon">
                    {/* Parchment scroll body */}
                    <path
                      d="M 8,8 L 42,8 L 42,52 L 8,52 Z"
                      fill="#f8f4e8"
                      stroke="#c4a77d"
                      strokeWidth="2"
                    />
                    {/* Scroll curls */}
                    <ellipse cx="8" cy="8" rx="4" ry="3" fill="#efe8d8" stroke="#c4a77d" strokeWidth="1.5" />
                    <ellipse cx="42" cy="8" rx="4" ry="3" fill="#efe8d8" stroke="#c4a77d" strokeWidth="1.5" />
                    <ellipse cx="8" cy="52" rx="4" ry="3" fill="#efe8d8" stroke="#c4a77d" strokeWidth="1.5" />
                    <ellipse cx="42" cy="52" rx="4" ry="3" fill="#efe8d8" stroke="#c4a77d" strokeWidth="1.5" />
                    {/* Text lines */}
                    <line x1="14" y1="20" x2="36" y2="20" stroke="#c4a77d" strokeWidth="1.2" opacity="0.6" />
                    <line x1="14" y1="28" x2="34" y2="28" stroke="#c4a77d" strokeWidth="1.2" opacity="0.6" />
                    <line x1="14" y1="36" x2="32" y2="36" stroke="#c4a77d" strokeWidth="1.2" opacity="0.6" />
                    <line x1="14" y1="44" x2="30" y2="44" stroke="#c4a77d" strokeWidth="1.2" opacity="0.6" />
                    {/* Heart decoration */}
                    <path
                      d="M 25,47 Q 23,45 21,47 Q 20,49 21,50 L 25,53 L 29,50 Q 30,49 29,47 Q 27,45 25,47 Z"
                      fill="#d9989f"
                    />
                  </svg>
                  <span>Words</span>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Coming Soon Section - Extended Greenspace with Flowers */}
        <div className="coming-soon-section">
          <div className="flower-field">
            {/* Decorative flowers rendered via CSS */}
          </div>
          <motion.button
            className="coming-soon-button"
            onClick={() => navigate('/coming-soon')}
            initial={{ opacity: 0, y: 150, scale: 0.8 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 100, damping: 15 }}
            whileHover={{ scale: 1.05, y: -8 }}
            whileTap={{ scale: 0.95 }}
          >
            Coming Soon
          </motion.button>
        </div>

        {/* Night Forest Section - Transition */}
        <div className="night-forest-section" />

        {/* Crimson Depths - Intimacy Entrance */}
        <div className="crimson-depths-section">
          <motion.button
            className="intimacy-heavy-button"
            onClick={() => navigate('/intimacy')}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            Intimacy
          </motion.button>
        </div>
      </div>

      {/* Otto Easter Egg - Orange Explosion */}
      <OrangeExplosion
        isActive={showOrangeExplosion}
        onComplete={() => setShowOrangeExplosion(false)}
      />

      {/* Punishment Popup Modal - Omi Only */}
      <AnimatePresence>
        {showPunishmentPopup && activePunishment && userProfile?.name === 'Omi' && (
          <motion.div
            className="punishment-popup-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="punishment-popup-modal"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
            >
              <div className="punishment-popup-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2>You Are Being Punished</h2>
              <p className="punishment-disappointment">
                Henry is disappointed in your submission. Your favor has fallen below acceptable levels.
              </p>
              <div className="punishment-details">
                <h3>Your Punishment:</h3>
                <p className="punishment-text">{activePunishment.text}</p>
              </div>
              <p className="punishment-notice">
                Your rewards are locked until this punishment is complete. Complete your punishment and earn Henry's forgiveness.
              </p>
              <button
                className="punishment-acknowledge-btn"
                onClick={() => {
                  setShowPunishmentPopup(false);
                  setPunishmentPopupDismissed(true);
                  sessionStorage.setItem('punishmentPopupDismissed', 'true');
                }}
              >
                I Understand
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Home;
